/**
 * SPEI specifics for the `v0-mx` flow — the Mexican-market rail that sits ALONGSIDE Stripe.
 *
 * SPEI is Mexico's interbank transfer rail: there is no card form and nothing to confirm in
 * the browser. The buyer is handed a set of BANK COORDINATES (a CLABE account number, a
 * payment reference, the beneficiary, the exact amount) and makes the transfer from their own
 * banking app. Settlement is asynchronous and confirmed server-side, minutes to a business day
 * later — so unlike the Stripe path, nothing here waits for a charge to succeed.
 *
 * The coordinates come from the SAME endpoint the Stripe checkout uses, switched to the SPEI
 * provider:
 *
 *     POST ${sessionApiUrl}/invoice
 *     { session_id, provider: "commerce-gate", full_name, voucher_code? }
 *
 * `full_name` is REQUIRED whenever `provider` is `commerce-gate` (the backend rejects the call
 * with 400 `"full_name is required for commerce-gate provider"` otherwise) — it is the name the
 * transfer must be registered under. Everything else (amount, currency, line items) is derived
 * server-side from the saved session, exactly like the Stripe path. See
 * [`docs/api.owners-hub.session.md`](../../../../../docs/api.owners-hub.session.md).
 *
 * Since the checkout gained the card rail, the invoice CALLS themselves (read, create, cancel,
 * reissue — on either provider) live in [`./checkout-invoice`](./checkout-invoice.ts), which is
 * also what keeps a session from ever holding the wrong rail's invoice. What stays here is the
 * SPEI-specific surface the flow imports by name: the auth header shared by those endpoints, the
 * coordinate mapping ([`./spei-details`](./spei-details.ts) — pure and unit-tested), and the
 * payment acknowledgement.
 */
import store from '@/store';
import {
  acknowledgePayment,
  isSessionStateRejection,
  type PaymentAcknowledgement,
} from '@/utils/session-payment';
import { COMMERCE_GATE_PROVIDER } from '@/utils/session-invoice';
import { hasPayableDetails, readSpeiDetails, type SpeiTransferDetails } from './spei-details';

export { hasPayableDetails, readSpeiDetails, COMMERCE_GATE_PROVIDER };
export type { SpeiTransferDetails };

/** Owner Bearer header for the invoice endpoints, read from the store (the shared Owners Hub
 *  convention is the RAW token, no `Bearer ` prefix). */
export function invoiceAuthHeaders(): Record<string, string> {
  const authToken = store.getState().authentication.access_token;
  return authToken ? { Authorization: authToken } : {};
}

/**
 * Payment acknowledgement — `POST ${sessionApiUrl}/payment-acknowledged` — is NOT SPEI-specific:
 * it is the transition that moves any paid session out of `configuration`, and the Stripe flows
 * (`v0`/`v3`) make the same call once their card charge clears. So it lives in
 * [`@/utils/session-payment`](../../../../utils/session-payment.ts) and is re-exported here, where
 * this flow's checkout already looks for its wiring. ONE definition of the endpoint, one mapping
 * of its response.
 *
 * On the SPEI rail it is called on the buyer's WORD ("I've made the transfer"), before the money
 * has moved — which is why `session.status === 'customization'` is not settlement evidence there.
 * On the card rail it follows a confirmed charge, exactly as in `v0`/`v3`. See this flow's README.
 */
export { acknowledgePayment, isSessionStateRejection };
export type { PaymentAcknowledgement };
